import { useCallback, useState } from 'react';

import feePaymentService from '../../services/feePaymentService';
import finePaymentService from '../../services/finePaymentService';
import { useAuth } from '../../context/AuthContext';
import { FEE_INVOICE_KIND_LABEL, FeePaymentDetail } from '../../types/fees';
import { FinePaymentDetail } from '../../types/letters';
import { PAYMENT_METHOD_LABEL } from '../../utils/paymentMethods';
import { usePdfExport, useSchoolBranding, fmtMoney } from '../../utils/pdf';
import ReceiptDocument, { ReceiptData } from './pdf/ReceiptDocument';

const nameOf = (v: any): string | undefined => (v && typeof v === 'object' ? v.name : undefined);

/** A fee payment → receipt: one line per challan it paid, plus any advance kept or used. */
const feeReceipt = (p: FeePaymentDetail, currency: string): ReceiptData => {
  const person = typeof p.personId === 'object' ? p.personId : undefined;
  const roll = typeof p.enrollmentId === 'object' ? p.enrollmentId?.rollNumber : undefined;
  return {
    title: 'Fee Receipt',
    number: p.receiptNumber,
    receivedOn: p.receivedAt,
    payer: [
      { label: 'Student', value: nameOf(p.studentId) || person?.name },
      { label: 'GR No.', value: person?.grNumber },
      // Admission fees: the application paid for — the applicant has no GR yet.
      { label: 'Application No.', value: p.applicationNumber },
      { label: 'Class', value: nameOf(p.classId) },
      { label: 'Roll No.', value: roll != null ? String(roll) : undefined },
    ],
    details: [
      { label: 'Received from', value: p.paidBy?.name },
      { label: 'Payment method', value: PAYMENT_METHOD_LABEL[p.method] },
      { label: 'Reference', value: p.referenceNumber },
    ],
    refHeader: 'Challan',
    lines: [
      ...p.allocations.map(a => ({
        ref: a.invoice?.invoiceNumber,
        description: [a.invoice?.kind && FEE_INVOICE_KIND_LABEL[a.invoice.kind], a.invoice?.period?.label].filter(Boolean).join(' · '),
        amount: a.amount,
      })),
      // Lines always add up to the money received now.
      ...(p.fromAdvance ? [{ ref: undefined, description: 'Paid from advance held', amount: -p.fromAdvance }] : []),
      ...(p.advanceAmount ? [{ ref: undefined, description: 'Kept as advance', amount: p.advanceAmount }] : []),
    ],
    amount: p.amount,
    currency,
    collectedBy: nameOf(p.collectedBy),
    reversed: p.isActive ? null : { at: p.reversedAt, by: nameOf(p.reversedBy), reason: p.reversalReason },
  };
};

/** A fine payment → receipt: one line for the fine, with what is left to pay. */
const fineReceipt = (p: FinePaymentDetail, currency: string): ReceiptData => {
  const fine = p.letterId;
  const balance = fine ? Math.max(0, (fine.amount || 0) - (fine.paidAmount || 0) - (fine.waivedAmount || 0)) : undefined;
  return {
    title: 'Fine Receipt',
    number: p.receiptNumber || p._id.slice(-6),
    receivedOn: p.collectedAt,
    payer: [
      { label: 'Student', value: p.studentId?.name },
      { label: 'GR No.', value: p.studentId?.grNumber },
    ],
    details: [
      { label: 'Payment method', value: PAYMENT_METHOD_LABEL[p.method] },
      { label: 'Reference', value: p.referenceNumber },
      { label: 'Still owed on this fine', value: balance !== undefined ? fmtMoney(balance, currency) : undefined },
    ],
    refHeader: 'Fine',
    lines: [{
      ref: fine?.categoryId?.label,
      description: fine?.title || fine?.reason || 'Fine',
      amount: p.amount,
    }],
    amount: p.amount,
    currency,
    collectedBy: nameOf(p.collectedBy),
    reversed: p.isActive ? null : { at: p.reversedAt, by: nameOf(p.reversedBy), reason: p.reversalReason },
  };
};

/**
 * Download a receipt PDF for any money received. Always rendered from the
 * payment as it stands now, so a reversed payment's receipt says so.
 */
export function useReceipts() {
  const { branding } = useSchoolBranding();
  const { currency: schoolCurrency } = useAuth();
  const currency = schoolCurrency || 'PKR';
  const { generate, loading } = usePdfExport();
  /** The payment whose receipt is being prepared — its download control shows a spinner. */
  const [preparingId, setPreparingId] = useState<string | null>(null);
  const preparing = useCallback(async (id: string, run: () => Promise<unknown>) => {
    setPreparingId(id);
    try {
      await run();
    } finally {
      setPreparingId(null);
    }
  }, []);

  const render = useCallback((data: ReceiptData) => (
    <ReceiptDocument data={data} branding={branding} generatedAt={new Date().toISOString()} />
  ), [branding]);

  const downloadFeeReceipt = useCallback(
    (payment: { _id: string; receiptNumber: string }) => preparing(payment._id, () => generate(async () => {
      const p = await feePaymentService.getFeePayment(payment._id);
      return render(feeReceipt(p, currency));
    }, `receipt-${payment.receiptNumber}.pdf`)),
    [currency, generate, render, preparing],
  );

  const downloadFineReceipt = useCallback(
    (payment: { _id: string; receiptNumber?: string }) => preparing(payment._id, () => generate(async () => {
      const p = await finePaymentService.getFinePayment(payment._id);
      return render(fineReceipt(p, currency));
    }, `receipt-${payment.receiptNumber || payment._id.slice(-6)}.pdf`)),
    [currency, generate, render, preparing],
  );

  return { downloadFeeReceipt, downloadFineReceipt, preparingReceipt: loading, preparingId };
}
